import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { registerUser, resetSuccess } from '../store/slices/authSlice';
import api from '../services/api';
import { formatApiError } from '../utils/errorMessage';

// Two registration paths share this page:
//
//   1. Self-serve (no `?invite=` query param). Behind the global
//      `allow_company_signup` gate. User picks a company name and gets
//      admin on a brand-new company pending HHMG approval.
//
//   2. Invited (`?invite=<token>`). Bypasses the gate. We fetch the
//      invitation preview to lock the email + show the company they're
//      joining; user just picks a username, password, and full name.
//      On submit the backend joins them to the inviting company and
//      auto-accepts the invitation in one transaction.
function Register() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const { loading, error, success } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    companyName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  });
  const [validationError, setValidationError] = useState('');
  const [signupAllowed, setSignupAllowed] = useState(null);
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    dispatch(resetSuccess());
  }, [dispatch]);

  // Self-serve gate — only relevant when there's no invitation token.
  // Invited users always get the form regardless of the gate.
  useEffect(() => {
    if (inviteToken) {
      setSignupAllowed(true);
      return;
    }
    api
      .get('/api/auth/signup-allowed')
      .then((res) => setSignupAllowed(!!res.data?.allowed))
      .catch(() => setSignupAllowed(false));
  }, [inviteToken]);

  // If invited, fetch the preview so we can lock the email field +
  // tell the user what they're signing up for.
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    api
      .get(`/api/users/invitations/${inviteToken}`)
      .then((res) => {
        if (cancelled) return;
        setInvitePreview(res.data);
        // Pre-fill known fields so the user only has to choose a
        // username + password + full name. The preview endpoint doesn't
        // return the email (it's privacy-sensitive — non-recipients
        // shouldn't see who was invited), so the user has to type it
        // and the backend will reject if it doesn't match the invite.
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e.response?.status;
        if (status === 410) setInviteError('This invitation is no longer valid (revoked, accepted, or expired). Ask the admin to send a fresh one.');
        else if (status === 404) setInviteError('Invitation not found. The link may have been mistyped.');
        else setInviteError(formatApiError(e, 'Could not load the invitation.'));
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  // After successful registration, route to login. Invited path the
  // backend already approved them, so they can sign in immediately.
  useEffect(() => {
    if (success) {
      const t = setTimeout(() => navigate('/login'), 2500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [success, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }
    if (formData.password.length < 10) {
      setValidationError('Password must be at least 10 characters long');
      return;
    }
    if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      setValidationError('Password must contain uppercase, lowercase, and a number');
      return;
    }
    if (!inviteToken && !formData.companyName.trim()) {
      setValidationError('Company name is required');
      return;
    }

    dispatch(registerUser({
      username: formData.username,
      email: formData.email,
      password: formData.password,
      fullName: formData.fullName,
      companyName: inviteToken ? undefined : formData.companyName,
      invitationToken: inviteToken || undefined,
    }));
  };

  // Loading the invitation preview / signup-gate check.
  if (signupAllowed === null || (inviteToken && !invitePreview && !inviteError)) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Invitation token present but invalid — explicit message instead of
  // dropping to the closed-signup wall.
  if (inviteToken && inviteError) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography component="h1" variant="h5" align="center" sx={{ mb: 2 }}>
              Invitation issue
            </Typography>
            <Alert severity="error" sx={{ mb: 2 }}>{inviteError}</Alert>
            <Button fullWidth variant="outlined" component={Link} to="/login">
              Back to sign in
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Self-serve closed and no invite — the only place we still show
  // "Signups closed".
  if (signupAllowed === false) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Typography component="h1" variant="h5" align="center" sx={{ mb: 2 }}>
              Signups closed
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              New account creation is currently disabled. To get access,
              ask an existing admin to send you an invitation — invited
              users can register even when signups are closed.
            </Typography>
            <Button fullWidth variant="contained" component={Link} to="/login">
              Back to sign in
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center">
            Watermarked Screeners
          </Typography>
          <Typography component="h2" variant="h6" align="center" sx={{ mt: 2 }}>
            {invitePreview ? 'Accept invitation' : 'Sign Up'}
          </Typography>

          {invitePreview && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You've been invited to join <strong>{invitePreview.company_name}</strong> as
              {' '}<strong>{(invitePreview.role || '').replace('_', ' ')}</strong>.
              Create an account with the invited email to accept.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}

          {validationError && (
            <Alert severity="error" sx={{ mt: 2 }}>{validationError}</Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {invitePreview
                ? `Account created and joined ${invitePreview.company_name}. Redirecting to sign in…`
                : 'Registration successful! Please wait for admin approval. Redirecting to login...'}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {!invitePreview && (
              <TextField
                margin="normal"
                required
                fullWidth
                id="companyName"
                label="Company Name"
                name="companyName"
                autoFocus
                value={formData.companyName}
                onChange={handleChange}
                helperText="Your production company. HHMG must approve before you can sign in."
              />
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              id="fullName"
              label="Your Full Name"
              name="fullName"
              autoComplete="name"
              autoFocus={!!invitePreview}
              value={formData.fullName}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              value={formData.username}
              onChange={handleChange}
              helperText="Used to sign in. Letters/numbers/underscores."
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              helperText={invitePreview ? 'Must match the email the invitation was sent to.' : undefined}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              helperText="At least 10 characters with upper, lower, and a number."
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || success}
            >
              {loading
                ? <CircularProgress size={24} />
                : (invitePreview ? 'Create account & accept' : 'Sign Up')}
            </Button>
            <Box textAlign="center">
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary">
                  Already have an account? Sign In
                </Typography>
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
}

export default Register;
